import { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Card,
  ProgressBar,
} from "react-bootstrap";
import { auth } from "../../../firebase";
import { updateDisplayName, sendPasswordResetEmailToCurrentUser } from "../../../firebase";
import { LicenseService, LicenseInfo } from "../../../services/licenseService";

interface UserProfile {
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
}

export default function AccountTab() {
  const [profile, setProfile] = useState<UserProfile>({
    displayName: "",
    email: "",
    photoURL: null,
  });
  const [nameInput, setNameInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // License state
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseError, setLicenseError] = useState<string | null>(null);

  useEffect(() => {
    loadUserProfile();
    fetchLicenseInfo();
  }, []);

  const loadUserProfile = () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setProfile({
        displayName: currentUser.displayName,
        email: currentUser.email,
        photoURL: currentUser.photoURL,
      });
      setNameInput(currentUser.displayName || "");
    }
  };

  const fetchLicenseInfo = async () => {
    setLicenseLoading(true);
    setLicenseError(null);

    try {
      const licenseData = await LicenseService.getUserLicenseInfo();
      console.log("Fetched license data:", licenseData);
      setLicense(licenseData);
    } catch (err: any) {
      console.error("Error fetching license info:", err);
      setLicenseError(err.message || "Failed to fetch license information");
    } finally {
      setLicenseLoading(false);
    }
  };

  const handleNameUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameInput.trim()) {
      setError("Name cannot be empty");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("No authenticated user found");
        return;
      }

      await updateDisplayName(nameInput.trim());

      setProfile(prev => ({
        ...prev,
        displayName: nameInput.trim()
      }));

      setSuccess("Your name has been updated successfully");
    } catch (err: any) {
      setError(err.message || "Failed to update name");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      setError("No authenticated user found");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await sendPasswordResetEmailToCurrentUser();
      setSuccess("Password reset email has been sent to your email address");
    } catch (err: any) {
      setError(err.message || "Failed to send password reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="p-0">
      {/* Success Message */}
      {success && (
        <Container className="mb-4 p-3" style={{
          backgroundColor: '#048a0420',
          border: '1px solid #048a0450',
          borderRadius: '8px'
        }}>
          <Row className="align-items-center">
            <Col xs="auto" className="pe-3">
              <div style={{ color: '#28a745', fontSize: '24px' }}>✅</div>
            </Col>
            <Col>
              <div className="text-success mb-2" style={{ fontWeight: '600' }}>
                Success
              </div>
              <div className="text-color mb-2" style={{ fontSize: '14px' }}>
                {success}
              </div>
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => setSuccess(null)}
                className="wind-btn-outline"
              >
                Dismiss
              </Button>
            </Col>
          </Row>
        </Container>
      )}

      {/* Error Message */}
      {error && (
        <Container className="mb-4 p-3" style={{
          backgroundColor: '#83030320',
          border: '1px solid #83030350',
          borderRadius: '8px'
        }}>
          <Row className="align-items-center">
            <Col xs="auto" className="pe-3">
              <div style={{ color: '#ff4444', fontSize: '24px' }}>⚠️</div>
            </Col>
            <Col>
              <div className="text-danger mb-2" style={{ fontWeight: '600' }}>
                Error
              </div>
              <div className="text-color mb-2" style={{ fontSize: '14px' }}>
                {error}
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setError(null)}
                className="wind-btn-outline"
              >
                Dismiss
              </Button>
            </Col>
          </Row>
        </Container>
      )}

      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="detail-page-heading mb-2">Account Settings</h2>
          <p className="detail-page-subheading">
            Manage your profile information and account security settings.
          </p>
        </Col>
      </Row>

      {/* Profile Information Section */}
      <Row className="mb-4">
        <Col>
          <Card className="background-color-light border-radius-4" style={{ border: '1px solid #2c3e50' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="detail-page-subheading mb-0" style={{ fontSize: '18px', color: '#067e93' }}>
                  👤 Profile Information
                </h5>
                {loading && <Spinner as="span" animation="border" size="sm" />}
              </div>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="mb-3">
                    <Form.Label className="text-color" style={{ fontWeight: '600', fontSize: '14px' }}>Email Address</Form.Label>
                    <div className="color-silver" style={{ fontSize: '16px' }}>
                      {profile.email || "No email address"}
                    </div>
                    <Form.Text className="text-muted" style={{ fontSize: '13px' }}>
                      Email address is read-only for security reasons.
                    </Form.Text>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Form.Label className="text-color" style={{ fontWeight: '600', fontSize: '14px' }}>Display Name</Form.Label>
                    <div className="color-silver" style={{ fontSize: '16px' }}>
                      {profile.displayName || "No display name"}
                    </div>
                  </div>
                </Col>
              </Row>

              <Form onSubmit={handleNameUpdate}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-color" style={{ fontWeight: '600', fontSize: '14px' }}>Update Display Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="Enter your name"
                    className="color-silver transparent-background search-text silver-border"
                    style={{ padding: '12px 16px' }}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '13px' }}>
                    This is your display name that will be shown across the application.
                  </Form.Text>
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || nameInput.trim() === profile.displayName}
                  className="wind-btn me-2"
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" />{" "}
                      Updating...
                    </>
                  ) : (
                    "Update Name"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

        {/* License Information */}
        <div className="mb-4">
          <Card className="background-color-light border-radius-4" style={{ border: '1px solid #2c3e50' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="detail-page-subheading mb-0" style={{ fontSize: '18px', color: '#067e93' }}>
                  📋 License Information
                </h5>
                {licenseLoading && <Spinner as="span" animation="border" size="sm" />}
              </div>

              {licenseError && (
                <div className="mb-3 p-2" style={{
                  backgroundColor: '#83030320',
                  border: '1px solid #83030350',
                  borderRadius: '4px'
                }}>
                  <div className="text-color" style={{ fontSize: '14px' }}>
                    ⚠️ {licenseError}
                  </div>
                </div>
              )}

              {license && !licenseLoading && (
                <div>
                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="mb-3">
                        <Form.Label className="text-color" style={{ fontWeight: '600', fontSize: '14px' }}>
                          License Plan
                        </Form.Label>
                        <div className="color-silver" style={{ fontSize: '16px' }}>
                          {LicenseService.formatLicensePlan(license.plan)}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Form.Label className="text-color" style={{ fontWeight: '600', fontSize: '14px' }}>
                          Account Role
                        </Form.Label>
                        <div className="color-silver" style={{ fontSize: '16px' }}>
                          {LicenseService.formatRole(license.role)}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="mb-3">
                        <Form.Label className="text-color" style={{ fontWeight: '600', fontSize: '14px' }}>
                          Usage
                        </Form.Label>
                        <div className="color-silver" style={{ fontSize: '16px' }}>
                          {LicenseService.formatUsageDisplay(license.usage, license.limits)}
                        </div>
                        {!LicenseService.isUnlimitedPlan(license.limits) && (
                          <>
                            <ProgressBar
                              now={LicenseService.getUsagePercentage(license.usage, license.limits)}
                              style={{
                                height: '8px',
                                marginTop: '8px',
                                backgroundColor: '#2c3e50'
                              }}
                              variant={
                                LicenseService.getUsagePercentage(license.usage, license.limits) >= 90 ? 'danger' :
                                LicenseService.getUsagePercentage(license.usage, license.limits) >= 70 ? 'warning' : 'success'
                              }
                            />
                            <div className="mt-1">
                              <span className="text-muted" style={{ fontSize: '12px' }}>
                                {license.remaining.toLocaleString()} requests remaining
                              </span>
                            </div>
                          </>
                        )}
                        {LicenseService.isUnlimitedPlan(license.limits) && (
                          <div className="mt-1">
                            <span className="text-muted" style={{ fontSize: '12px' }}>
                              Unlimited requests available
                            </span>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Form.Label className="text-color" style={{ fontWeight: '600', fontSize: '14px' }}>
                          Status
                        </Form.Label>
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: LicenseService.getUsageStatus(license.usage, license.limits).color,
                              marginRight: '8px'
                            }}
                          />
                          <span className="color-silver" style={{ fontSize: '16px' }}>
                            {LicenseService.getUsageStatus(license.usage, license.limits).status}
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>

                                  </div>
              )}
            </Card.Body>
          </Card>
        </div>

      {/* Password Management Section */}
      <Row className="mb-4">
        <Col>
          <Card className="background-color-light border-radius-4" style={{ border: '1px solid #2c3e50' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="detail-page-subheading mb-0" style={{ fontSize: '18px', color: '#067e93' }}>
                  🔒 Password Management
                </h5>
                {loading && <Spinner as="span" animation="border" size="sm" />}
              </div>

              <p className="text-color mb-4" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                Click the button below to receive a password reset email. You'll be able to
                create a new password using the secure link sent to your email address.
              </p>

              <Button
                variant="outline-primary"
                onClick={handlePasswordReset}
                disabled={loading}
                className="wind-btn-outline"
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" />{" "}
                    Sending Email...
                  </>
                ) : (
                  "📧 Send Password Reset Email"
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Profile Picture Section - Placeholder for Future */}
      <Row className="mb-4">
        <Col>
          <Card className="background-color-light border-radius-4" style={{ border: '1px solid #2c3e50' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="detail-page-subheading mb-0" style={{ fontSize: '18px', color: '#067e93' }}>
                  📷 Profile Picture
                </h5>
              </div>

              <div className="text-center py-3">
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>👤</div>
                <p className="text-color mb-4" style={{ fontSize: '14px' }}>
                  Profile picture functionality will be available in a future update.
                </p>
                <Button variant="secondary" disabled style={{ opacity: 0.6 }}>
                  Coming Soon
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}